import {
    IHttpActionOptions,
    IRequestHeaders,
    IFetchOptions,
} from '../interfaces';
import { HttpMethod } from '../types';
import { RestBadHttpActionParams, RestClientError } from '../helpers/errors';

export default class FetchClientService {
    private static validateEndpoint(endpoint: string) {
        if (endpoint.startsWith('/')) return;
        const errorMessage = `Invalid endpoint "${endpoint}". Please replace with "/${endpoint}"`;
        throw new RestBadHttpActionParams(endpoint, errorMessage);
    }

    private static buildUrl(endpoint: string, options: IHttpActionOptions) {
        const baseDomain: string = process.env.DOMAIN ?? '';
        const privateDomain: string = process.env.PRIVATE_DOMAIN ?? '';
        const defaultDomain =
            options.privateDomain === false ? baseDomain : privateDomain;
        return `${options.baseDomain || defaultDomain}${endpoint}`;
    }

    private static baseHeaders(options: IHttpActionOptions) {
        const baseHeaders: IRequestHeaders = {
            'Content-Type': 'application/json',
        };

        const customContentTypeHeader =
            options.headers?.['content-type'] ||
            options.headers?.['Content-Type'];

        if (customContentTypeHeader) {
            baseHeaders['Content-Type'] = customContentTypeHeader;
        }

        if (options.useApiKey && process.env.API_KEY)
            baseHeaders['x-api-key'] = process.env.API_KEY;
        return baseHeaders;
    }

    private static customHeaders(options: IHttpActionOptions) {
        const {
            // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
            'Content-Type': _ContentType,
            // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
            'content-type': _contentType,
            ...customHeadersObject
        } = options.headers || {};

        return customHeadersObject;
    }

    private static buildFetchOptions(
        method: string,
        body: unknown,
        options: IHttpActionOptions,
    ): IFetchOptions {
        const headers = {
            ...this.baseHeaders(options),
            ...this.customHeaders(options),
        };

        /**
         * * exclude headers with invalid values
         */
        const filteredHeaders = Object.fromEntries(
            Object.entries(headers).filter(
                ([, value]) => value !== null && value !== undefined,
            ),
        );

        const requestOptions: IFetchOptions = {
            headers: filteredHeaders,
            method,
        };
        if (body) requestOptions['body'] = JSON.stringify(body);
        return requestOptions;
    }

    private static isValidUrl(url: URL) {
        return !!(url.protocol && url.hostname && url.pathname);
    }

    private static hasValidProtocol(url: URL) {
        const secureProtocols = ['http:', 'https:'];
        return secureProtocols.includes(url.protocol);
    }

    private static verifyUrl(urlObj: URL): void {
        if (!this.isValidUrl(urlObj)) {
            throw new RestBadHttpActionParams(
                'Invalid URL',
                `URL details: Protocol: ${urlObj.protocol}, Hostname: ${urlObj.hostname}, Pathname: ${urlObj.pathname}`,
            );
        }
        if (!this.hasValidProtocol(urlObj)) {
            throw new RestBadHttpActionParams(
                'Invalid URL',
                `URL protocol [${urlObj.protocol}] is not secure`,
            );
        }
    }

    private static toHttpUrl(url: string) {
        try {
            const urlObj = new URL(url);
            this.verifyUrl(urlObj);
            return urlObj.href;
        } catch (error) {
            throw new RestBadHttpActionParams(
                `Invalid URL [${url}]`,
                'URL is not valid.',
                error,
            );
        }
    }

    private static async getResponseData(
        isJsonResponse: boolean,
        response: Response,
    ) {
        const clonedResponse = response.clone();
        let responseData = null;
        try {
            if (isJsonResponse) {
                responseData = await response.json();
            } else {
                responseData = await response.text();
            }
            await clonedResponse.text();
        } catch (_) {
            responseData = await clonedResponse.text();
        }
        return responseData;
    }

    private static async handleResponse(response: Response): Promise<unknown> {
        const responseContentType = response.headers.get('content-type');
        const isJsonResponse =
            responseContentType &&
            responseContentType.includes('application/json');

        const responseData = await this.getResponseData(
            !!isJsonResponse,
            response,
        );

        if (response.ok) {
            return responseData;
        }
        throw new RestClientError(response.url, responseData);
    }

    private static fetchData(url: string, requestOptions: IFetchOptions) {
        return fetch(url, requestOptions)
            .then(this.handleResponse.bind(this))
            .catch((error) => {
                if (error instanceof RestClientError) throw error;
                throw new RestClientError(url, error);
            });
    }

    private static queryParamsToQueryString(
        queryParameters: Record<string, string | string[]>,
    ) {
        const queryParamsArray = Object.entries(queryParameters).map(
            ([queryName, queryValue]) => {
                const isArrayValue = Array.isArray(queryValue);
                if (isArrayValue)
                    return queryValue
                        .map((val) => `${queryName}=${val}`)
                        .join('&');
                else return `${queryName}=${queryValue}`;
            },
        );
        return queryParamsArray.join('&');
    }

    public static httpAction(
        method: HttpMethod,
        endpoint: string,
        body: unknown,
        options: IHttpActionOptions = {},
    ) {
        this.validateEndpoint(endpoint);
        const url = this.buildUrl(endpoint, options);
        const queryParamsString = options.queryParameters
            ? `?${this.queryParamsToQueryString(options.queryParameters)}`
            : '';
        const httpUrl = this.toHttpUrl(`${url}${queryParamsString}`);
        return this.fetchData(
            httpUrl,
            this.buildFetchOptions(method.toUpperCase(), body, options),
        );
    }
}
